import { Col, Row, Spin, Tree, notification, Button, Icon, Input } from 'antd';
import React, { Component, Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { fetchGedCatalog, fetchGedFile } from '../../utils/apiBucherVaslin';
import { extensionToType } from '../../utils/helpers';
import { isInfoCompleted } from '../../utils/helpers';
import PageLayout from '../Layout/PageLayout';
import GedFileViewer from './GedFileViewer';
import TablePDRGedCatalog from './TablePDRGedCatalog';
import './GedCatalog.less';

const { DirectoryTree, TreeNode } = Tree;
const { Search } = Input;

// The GED tree can be huge, so we wait for the user to stop typing before
// filtering it, and only ever filter it once per debounced value.
const SEARCH_DEBOUNCE_MS = 800;
const SEARCH_MIN_LENGTH = 3;
// Number of nodes visited per macrotask: filtering yields between chunks so
// the input stays responsive (and interruptible) while it works through a
// huge tree, instead of blocking the main thread until it's fully done.
const SEARCH_CHUNK_SIZE = 300;

// Rendering the (possibly huge) tree into TreeNode elements is expensive, so
// this is split into its own PureComponent: as long as GedCatalog passes it
// stable props (same tree/expandedKeys/highlight references), it skips
// re-rendering entirely on every keystroke in the search box, which is what
// was freezing the input while typing.
class GedDirectoryTreeView extends PureComponent {
    getNodeIcon = (node) => {
        if (node.IsDir) {
            return <Icon type="folder" />;
        }

        const fileName = node.Name || '';
        const fileExtension = fileName.split('.').pop().toLowerCase();
        const fileType = extensionToType(fileExtension);

        let iconType = 'file';
        switch (fileType) {
            case 'image':
                iconType = 'file-image';
                break;
            case 'pdf':
                iconType = 'file-pdf';
                break;
            case 'excel':
                iconType = 'file-excel';
                break;
            case 'word':
                iconType = 'file-word';
                break;
            default:
                iconType = 'file';
                break;
        }

        return <Icon type={iconType} />;
    };

    highlightMatch = (name, searchValue) => {
        if (!searchValue) return name;

        const lowerName = name.toLowerCase();
        const lowerSearch = searchValue.toLowerCase();
        const index = lowerName.indexOf(lowerSearch);

        if (index === -1) return name;

        return (
            <span>
                {name.substring(0, index)}
                <span className="search-highlight">{name.substring(index, index + searchValue.length)}</span>
                {name.substring(index + searchValue.length)}
            </span>
        );
    };

    renderNodes = (nodes, searchValue) => {
        if (!nodes || nodes.length === 0) return null;

        return nodes.map((node) => {
            if (!node) return null;

            const title = this.highlightMatch(node.Name, searchValue);
            const key = `${node.IsDir ? 'DIR' : 'FILE'}_${node.Id}`;
            const icon = this.getNodeIcon(node);

            if (node.IsDir && node.Children && node.Children.length > 0) {
                return (
                    <TreeNode
                        key={key}
                        title={<span style={{ fontWeight: 'bold' }}>{title}</span>}
                        isDir={true}
                        icon={<Icon type="folder" />}
                    >
                        {this.renderNodes(node.Children, searchValue)}
                    </TreeNode>
                );
            }

            if (node.IsDir) {
                return (
                    <TreeNode
                        key={key}
                        title={title}
                        isDir={true}
                        icon={<Icon type="folder" />}
                    />
                );
            }

            return (
                <TreeNode
                    key={key}
                    title={title}
                    isDir={false}
                    fileId={node.Id}
                    fileData={node}
                    icon={icon}
                    isLeaf
                />
            );
        });
    };

    render() {
        const { nodes, expandedKeys, selectedNodeKey, highlightValue, onExpand, onSelect } = this.props;

        return (
            <DirectoryTree
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                onSelect={onSelect}
                selectedKeys={selectedNodeKey ? [selectedNodeKey] : []}
            >
                {this.renderNodes(nodes, highlightValue)}
            </DirectoryTree>
        );
    }
}

class GedCatalog extends Component {
    state = {
        expandedKeys: [],
        tree: null,
        catalogLoading: true,
        fileLoading: false,
        selectedFileData: null,
        selectedFileBlob: null,
        selectedNodeKey: null,
        affichePDR: false,
        tablePdrActions: null,
        downloadDisabled: false,
        searchValue: '',
        appliedSearchValue: '',
        filteredTree: null,
        searchLoading: false
    };

    async componentDidMount() {
        const { intl, userID } = this.props;

        // Vérifier la connexion et les informations utilisateur
        await isInfoCompleted(intl, userID);
        this.fetchCatalog();
    }

    componentWillUnmount() {
        clearTimeout(this.searchDebounceTimer);
        clearTimeout(this.searchComputeTimer);
        // Bump the token so any chunk still scheduled from a previous run
        // recognizes itself as stale and stops instead of calling setState.
        this.searchRunToken = (this.searchRunToken || 0) + 1;
    }

    fetchCatalog = async () => {
        const { intl } = this.props;

        try {
            const { isSuccess, gedFiles, isUnauthorized, errorDetail } = await fetchGedCatalog();
            if (!isSuccess) {
                notification.error({
                    message: isUnauthorized
                        ? intl.formatMessage({ id: 'error.unauthorized.title' })
                        : intl.formatMessage({ id: 'common.error' }),
                    description: isUnauthorized
                        ? intl.formatMessage({ id: 'error.unauthorized.message' })
                        : errorDetail
                });
                this.setState({ catalogLoading: false });
                return;
            }

            this.setState({
                tree: gedFiles,
                catalogLoading: false
            });
        } catch (error) {
            notification.error({
                message: intl.formatMessage({ id: 'common.error' }),
                description: error.message
            });
            this.setState({ catalogLoading: false });
        }
    };

    registerTablePdrActions = (tablePdrActions) => {
        this.setState({ tablePdrActions });
    };

    // Keys the user explicitly collapsed while a search is active. Not part
    // of state on purpose: it must survive across debounced re-searches
    // without itself triggering a render, and is reset whenever the search
    // is cleared (see onSearchChange).
    collapsedDuringSearch = new Set();

    onExpand = (expandedKeys) => {
        const previousKeys = this.state.expandedKeys;

        previousKeys.forEach((key) => {
            if (!expandedKeys.includes(key)) {
                this.collapsedDuringSearch.add(key);
            }
        });
        expandedKeys.forEach((key) => {
            this.collapsedDuringSearch.delete(key);
        });

        this.setState({
            expandedKeys,
            autoExpandParent: false
        });
    };

    onSearchChange = (e) => {
        const searchValue = e.target.value;
        this.setState({ searchValue });

        // Any previous run (debounce still pending, or a chunked filter
        // already in progress) is abandoned as soon as the input changes.
        clearTimeout(this.searchDebounceTimer);
        clearTimeout(this.searchComputeTimer);
        this.searchRunToken = (this.searchRunToken || 0) + 1;

        if (searchValue.trim().length < SEARCH_MIN_LENGTH) {
            this.collapsedDuringSearch = new Set();

            // Fully clearing the search bar retracts every branch instead of
            // leaving them however the search left them.
            const clearedState = searchValue.trim().length < 3 ? { expandedKeys: [] } : null;

            this.setState({
                searchLoading: false,
                appliedSearchValue: '',
                filteredTree: null,
                ...clearedState
            });
            return;
        }

        this.searchDebounceTimer = setTimeout(() => {
            this.runSearch(searchValue);
        }, SEARCH_DEBOUNCE_MS);
    };

    // Starts a fresh, cancellable filtering run. The token captured here is
    // compared against this.searchRunToken at every chunk: as soon as the
    // user changes the search again, the token is bumped and this run drops
    // itself instead of finishing (same idea as RxJS switchMap).
    runSearch = (searchValue) => {
        const token = (this.searchRunToken = (this.searchRunToken || 0) + 1);
        this.setState({ searchLoading: true });

        const generator = this.filterTreeChunked(this.state.tree, searchValue.toLowerCase());

        const step = () => {
            if (token !== this.searchRunToken) return;

            let next;
            for (let i = 0; i < SEARCH_CHUNK_SIZE; i += 1) {
                next = generator.next();
                if (next.done) break;
            }

            if (token !== this.searchRunToken) return;

            if (!next.done) {
                // Still work to do: yield to the browser (lets it repaint and
                // handle pending input/keystrokes) before the next chunk.
                this.searchComputeTimer = setTimeout(step, 0);
                return;
            }

            const filteredTree = next.value;

            // Auto-expand the matching branches like before, but skip any
            // branch the user explicitly collapsed during this search
            // session so refining the search doesn't force it back open.
            this.setState((prevState) => {
                const autoExpand = this.collectExpandableKeys(filteredTree)
                    .filter((key) => !this.collapsedDuringSearch.has(key));
                const expandedKeys = Array.from(new Set([...prevState.expandedKeys, ...autoExpand]));

                return {
                    filteredTree,
                    expandedKeys,
                    appliedSearchValue: searchValue,
                    searchLoading: false
                };
            });
        };

        step();
    };

    // Generator version of the tree filter: yields once per visited node so
    // the caller can pause between chunks. Keeps a node when its own name
    // matches, or filters its children to keep only the branches leading to
    // a match. A matching folder keeps its full subtree, since the user is
    // looking for that folder specifically.
    *filterTreeChunked(nodes, lowerSearch) {
        if (!nodes) return [];

        const result = [];

        for (const node of nodes) {
            if (!node) continue;

            yield;

            const nameMatches = node.Name && node.Name.toLowerCase().includes(lowerSearch);

            if (node.IsDir) {
                if (nameMatches) {
                    result.push(node);
                    continue;
                }

                const filteredChildren = yield* this.filterTreeChunked(node.Children, lowerSearch);
                if (filteredChildren.length > 0) {
                    result.push({ ...node, Children: filteredChildren });
                }
                continue;
            }

            if (nameMatches) {
                result.push(node);
            }
        }

        return result;
    }

    // Collects the keys of every folder present in a (filtered) tree, so all
    // of them can be auto-expanded to reveal the search matches.
    collectExpandableKeys = (nodes) => {
        if (!nodes) return [];

        return nodes.reduce((keys, node) => {
            if (!node || !node.IsDir) return keys;

            keys.push(`DIR_${node.Id}`);
            return keys.concat(this.collectExpandableKeys(node.Children));
        }, []);
    };

    onNodeSelected = async (selectedKeys, info) => {
        if (selectedKeys.length === 0) return;
        const nodeKey = selectedKeys[0];
        const selectedNode = info.selectedNodes[0];

        if (selectedNode.props.isDir) {
            this.setState({ selectedNodeKey: nodeKey });
            return;
        }

        this.fetchFile(selectedNode.props.fileId, nodeKey, selectedNode.props.fileData);
    };

    fetchFile = async (fileId, nodeKey, fileData) => {
        const { intl } = this.props;

        try {
            this.setState({ fileLoading: true });

            const { isSuccess, file, isUnauthorized, errorDetail } = await fetchGedFile(fileId);

            if (!isSuccess) {
                notification.error({
                    message: isUnauthorized
                        ? intl.formatMessage({ id: 'error.unauthorized.title' })
                        : intl.formatMessage({ id: 'common.error' }),
                    description: isUnauthorized
                        ? intl.formatMessage({ id: 'error.unauthorized.message' })
                        : errorDetail
                });
                this.setState({
                    selectedFileData: {
                        ...(fileData || {}),
                        Id: fileId,
                        Name: (fileData && fileData.Name) || `document-${fileId}`
                    },
                    selectedFileBlob: null,
                    selectedNodeKey: nodeKey,
                    affichePDR: true,
                    downloadDisabled: true,
                    fileLoading: false
                });
                return;
            }

            this.setState({
                selectedFileData: {
                    ...(fileData || {}),
                    Id: fileId,
                    Name: (fileData && fileData.Name) || `document-${fileId}`
                },
                selectedFileBlob: file,
                selectedNodeKey: nodeKey,
                affichePDR: true,
                downloadDisabled: false,
                fileLoading: false
            });

        } catch (error) {
            notification.error({
                message: intl.formatMessage({ id: 'common.error' }),
                description: error.message
            });
            this.setState({ fileLoading: false });
        }
    };

    downloadFile = async () => {
        const { intl } = this.props;
        const { selectedFileData, selectedFileBlob } = this.state;

        if (!selectedFileData || !selectedFileData.Id) return;

        try {
            let fileBlob = selectedFileBlob;

            if (!fileBlob) {
                const { isSuccess, file, isUnauthorized, errorDetail } = await fetchGedFile(selectedFileData.Id);

                if (!isSuccess) {
                    notification.error({
                        message: isUnauthorized
                            ? intl.formatMessage({ id: 'error.unauthorized.title' })
                            : intl.formatMessage({ id: 'common.error' }),
                        description: isUnauthorized
                            ? intl.formatMessage({ id: 'error.unauthorized.message' })
                            : errorDetail
                    });
                    this.setState({ downloadDisabled: true });
                    return;
                }

                fileBlob = file;
            }

            const fileName = selectedFileData.Name;
            const blobUrl = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');

            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            notification.error({
                message: <FormattedMessage id="common.error" />,
                description: error.message
            });
        }
    };

    render() {
        const { history, intl } = this.props;
        const {
            tree, catalogLoading, fileLoading, expandedKeys, selectedFileData, selectedFileBlob,
            selectedNodeKey, affichePDR, tablePdrActions, downloadDisabled, searchValue,
            appliedSearchValue, filteredTree, searchLoading
        } = this.state;

        // While the debounce is pending (or before the first result of a new
        // search has been computed), keep showing whatever was displayed
        // before instead of blanking the tree or recomputing on every key.
        // expandedKeys is a single source of truth in both modes, so folders
        // the user manually collapses during a search stay collapsed.
        const isSearching = searchValue.trim().length > 0;
        const hasFilteredResult = isSearching && filteredTree !== null;
        const displayTree = hasFilteredResult ? filteredTree : tree;
        const highlightValue = hasFilteredResult ? appliedSearchValue : '';
        const isBelowMinLength = isSearching && searchValue.trim().length < SEARCH_MIN_LENGTH;

        return (
            <PageLayout pageKey="catalog" history={history}>
                <div className="ged-catalog-container">
                    <Row className="ged-explorator" gutter={16}>
                        <Col span={6} className="ged-tree-view">
                            {catalogLoading ? (
                                <div className="section-loading">
                                    <Spin />
                                </div>
                            ) : (
                                <Fragment>
                                    <Search
                                        className="tree-search"
                                        placeholder={intl.formatMessage({ id: 'catalog.filter.placeholder' })}
                                        value={searchValue}
                                        onChange={this.onSearchChange}
                                        allowClear
                                    />
                                    {isBelowMinLength && (
                                        <div className="tree-search-hint">
                                            <FormattedMessage id="catalog.filter.minLength" values={{ min: SEARCH_MIN_LENGTH }} />
                                        </div>
                                    )}
                                    {searchLoading ? (
                                        <div className="section-loading">
                                            <Spin />
                                        </div>
                                    ) : (
                                        displayTree && (
                                            <GedDirectoryTreeView
                                                nodes={displayTree}
                                                expandedKeys={expandedKeys}
                                                selectedNodeKey={selectedNodeKey}
                                                highlightValue={highlightValue}
                                                onExpand={this.onExpand}
                                                onSelect={this.onNodeSelected}
                                            />
                                        )
                                    )}
                                </Fragment>
                            )}
                        </Col>
                        <Col span={18} className="ged-file-view">
                            {fileLoading ? (
                                <div className="section-loading">
                                    <Spin />
                                </div>
                            ) : selectedFileData ? (
                                <div className="file-view-content">
                                    <div className="file-header">
                                        <h3>{selectedFileData.Name}</h3>

                                        <div>
                                            {affichePDR && tablePdrActions && selectedFileData.Desc && (
                                                <Button style={{ marginRight: 8 }} onClick={tablePdrActions.openConsultOrder}>
                                                    <FormattedMessage id="catalog.tablePdr.button.consultOrder" />
                                                </Button>
                                            )}

                                            <Button type="primary" disabled={downloadDisabled} onClick={this.downloadFile}>
                                                <Icon type="download" />
                                                <FormattedMessage id="documents.download" />
                                            </Button>
                                        </div>
                                    </div>
                                    <GedFileViewer file={selectedFileData} fileBlob={selectedFileBlob} />
                                    <TablePDRGedCatalog
                                        file={selectedFileData}
                                        affichePDR={affichePDR}
                                        registerActions={this.registerTablePdrActions}
                                    />
                                </div>
                            ) : (
                                <div className="file-placeholder">
                                    <FormattedMessage id="documents.selectfile" />
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </PageLayout>
        );
    }
}

GedCatalog.propTypes = {
    intl: intlShape.isRequired
};

const mapStateToProps = ({ app: { locale }, signIn: { userID } }) => ({
    locale,
    userID
});

export default connect(mapStateToProps)(injectIntl(GedCatalog));
